import { useEffect } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface UseScheduleKeyboardShortcutsOptions {
  activeView: string;
  setActiveView: (view: string) => void;
  bulkMode: boolean;
  setBulkMode: (fn: (prev: boolean) => boolean) => void;
  setBulkSelectedCourseIds: (fn: (prev: string[]) => string[]) => void;
  selectAllVisibleForBulk: () => void;
  nextWeek: () => void;
  previousWeek: () => void;
  setCreateDialogOpen: (open: boolean) => void;
  selectedTeacher: string | number | undefined;
  teacherLabel: string;
  setTrialPrefill: (prefill: {
    teacherId?: string | number;
    date?: string;
    startTime?: string;
    dayOfWeek?: number;
    requireDateSelection?: boolean;
  }) => void;
  setTrialDialogOpen: (open: boolean) => void;
}

export function useScheduleKeyboardShortcuts({
  activeView,
  setActiveView,
  bulkMode,
  setBulkMode,
  setBulkSelectedCourseIds,
  selectAllVisibleForBulk,
  nextWeek,
  previousWeek,
  setCreateDialogOpen,
  selectedTeacher,
  teacherLabel,
  setTrialPrefill,
  setTrialDialogOpen,
}: UseScheduleKeyboardShortcutsOptions) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTyping =
        target?.tagName === 'INPUT'
        || target?.tagName === 'TEXTAREA'
        || target?.tagName === 'SELECT'
        || !!target?.closest('[contenteditable="true"]');
      if (isTyping) return;

      if (event.key === '1') {
        setActiveView('calendar');
      } else if (event.key === '2') {
        setActiveView('scheduler');
      } else if (event.key === '3') {
        setActiveView('resources');
      } else if (event.key.toLowerCase() === 'b') {
        setBulkMode((prev) => !prev);
      } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
        if (!bulkMode) return;
        event.preventDefault();
        selectAllVisibleForBulk();
      } else if (event.key === 'Escape') {
        setBulkSelectedCourseIds(() => []);
      } else if (event.key === 'ArrowLeft') {
        if (activeView === 'calendar' || activeView === 'resources') {
          event.preventDefault();
          previousWeek();
        }
      } else if (event.key === 'ArrowRight') {
        if (activeView === 'calendar' || activeView === 'resources') {
          event.preventDefault();
          nextWeek();
        }
      } else if (event.key.toLowerCase() === 'n') {
        setCreateDialogOpen(true);
      } else if (event.key.toLowerCase() === 't') {
        if (!selectedTeacher) {
          toast.error('Bitte zuerst einen ' + teacherLabel + ' auswählen');
          return;
        }
        setTrialPrefill({
          teacherId: selectedTeacher,
          date: activeView === 'calendar' ? format(new Date(), 'yyyy-MM-dd') : undefined,
          startTime: '09:00',
          dayOfWeek: activeView === 'calendar' ? ((new Date().getDay() + 6) % 7) + 1 : undefined,
          requireDateSelection: activeView !== 'calendar',
        });
        setTrialDialogOpen(true);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [activeView, bulkMode, nextWeek, previousWeek, selectAllVisibleForBulk, selectedTeacher, teacherLabel, setActiveView, setBulkMode, setBulkSelectedCourseIds, setCreateDialogOpen, setTrialPrefill, setTrialDialogOpen]);
}
